import ls from '@livesession/sdk'
import { Badge, Button } from '@mui/material'
import { ArrowDropDownIcon } from '@mui/x-date-pickers'
import _ from 'lodash'
import React, { useState } from 'react'
import styles from './ProductFilter.module.scss'
import ProductFilterModal from './ProductFilterModal/ProductFilterModal'
import { ActiveFiltersInterface, ProductFiltersInterface } from '../../interfaces/FiltersInterface'
import { countActiveFilters, putAttribute, putFlatAttribute, putRangeFilter } from '../../utils/productFilter'
import ErrorBoundary from '../ErrorBoundary/ErrorBoundary'
import FormattedMessage from '../FormattedMessage/FormattedMessage'

interface Props {
  filters: ProductFiltersInterface
  activeFilters: ActiveFiltersInterface
  loading: boolean
  itemsCounter: number
  onFilterChange: (filters: ActiveFiltersInterface) => void
}

const ProductFilter: React.FC<Props> = (props: Props) => {
  const [showFiltersModal, setShowFiltersModal] = useState<boolean>(false)

  const toggleFilterModal = () => {
    setShowFiltersModal(!showFiltersModal)
  }

  const handleFilterModalClose = () => {
    setShowFiltersModal(false)
  }

  const handleAttributeChange = (attributeId: number, valueId: number, checked: boolean) => {
    const newFilters = putAttribute(props.activeFilters, attributeId, valueId, checked)
    props.onFilterChange(newFilters)
    ls.track('Filter Attribute Used', {
      attributeId_int: attributeId,
      valueId_str: valueId,
      checked_bool: checked,
    })
  }

  const handleFlatAttributeChange = (name: string, valueId: number | string, checked: boolean) => {
    const newFilters = putFlatAttribute(props.activeFilters, name, valueId, checked)
    props.onFilterChange(newFilters)
    ls.track('Filter Flat Used', {
      name_str: name,
      valueId_str: valueId,
      checked_bool: checked || false,
    })
  }

  const handleRangeChanged = (name: string, min: string | null, max: string | null, extra?: object) => {
    const newFilters = putRangeFilter(props.activeFilters, name, min, max, extra)
    props.onFilterChange(newFilters)
    ls.track('Filter Range Used', {
      name_str: name,
      minValue_str: min,
      maxValue_str: max,
    })
  }

  const clearFilters = () => {
    props.onFilterChange({})
    ls.track('Filters Cleared')
  }

  return (
    <ErrorBoundary>
      <Badge
        badgeContent={countActiveFilters(props.activeFilters)}
        color="primary"
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        classes={{
          anchorOriginTopRight: styles['toggle-button__badge'],
        }}
      >
        <Button variant="contained" className={styles['toggle-button--mobile']} disableElevation onClick={toggleFilterModal}>
          <FormattedMessage id="ProductFilter.ToggleButton" defaultMessage="Filters" />
        </Button>
        <Button variant="text" className={styles['toggle-button--desktop']} disableElevation onClick={toggleFilterModal}>
          <FormattedMessage id="ProductFilter.ToggleButton" defaultMessage="Filters" />
          <ArrowDropDownIcon />
        </Button>
      </Badge>

      <ProductFilterModal
        open={showFiltersModal}
        onClose={handleFilterModalClose}
        filters={props.filters}
        activeFilters={props.activeFilters}
        loading={props.loading}
        itemsCounter={props.itemsCounter}
        onFlatAttributeChange={handleFlatAttributeChange}
        onAttributeChange={handleAttributeChange}
        onRangeChanged={handleRangeChanged}
        clearFilters={clearFilters}
      />
    </ErrorBoundary>
  )
}

export default ProductFilter
